// L'EFFACEUR — Panel admin Notifications ON/OFF + tests Brevo

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Mail, MessageSquare, Send, RefreshCw, Power } from 'lucide-react';
import { API } from '../../App';
import { Button } from '../ui/button';

const NotificationsTogglePanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/notifications/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
    } catch {
      toast.error('Erreur chargement statut notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const toggle = async (channel, newValue) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const body = channel === 'email' ? { email_enabled: newValue } : { sms_enabled: newValue };
      const res = await axios.post(`${API}/admin/notifications/toggle`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const label = channel === 'email' ? 'Email' : 'SMS';
      toast.success(`${label} ${newValue ? 'activé' : 'désactivé'}`);
      setStatus({ ...status, [`${channel}_enabled`]: res.data[`${channel}_enabled`] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur toggle');
    } finally {
      setSubmitting(false);
    }
  };

  const sendTest = async (channel) => {
    setTestResult(null);
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = channel === 'email' ? 'test-email' : 'test-sms';
      const res = await axios.post(`${API}/admin/notifications/${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTestResult({ channel, ...res.data });
      if (res.data.status === 'sent') {
        toast.success(`Test ${channel} envoyé à ${res.data.to}`);
      } else {
        toast.error(`Test ${channel} échoué : ${res.data.error}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur test');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-gray-500 font-mono text-sm">Chargement...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-1 h-8 bg-cyan-400 rounded-full" style={{ boxShadow: '0 0 10px rgba(0,245,255,0.8)' }} />
          <h2 className="font-orbitron text-xl font-black text-white">Notifications</h2>
          <span className="font-mono text-xs text-cyan-400/60">// EMAIL · SMS · TOGGLES</span>
        </div>
        <Button onClick={fetchStatus} variant="outline" className="text-xs">
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* EMAIL */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className={`glass-card border p-6 ${status?.email_enabled ? 'border-success/40' : 'border-gray-700'}`}>
          <div className="flex items-center gap-3 mb-4">
            <Mail className={`w-8 h-8 ${status?.email_enabled ? 'text-success' : 'text-gray-500'}`} />
            <div>
              <h3 className="text-white font-bold">Email (Brevo)</h3>
              <p className="text-xs text-gray-500">Gratuit jusqu'à 300 mails/jour · Au-delà ~0,002€/mail</p>
            </div>
          </div>
          <div className="text-sm text-gray-400 mb-4">
            Statut : <span className={`font-bold ${status?.email_enabled ? 'text-success' : 'text-red-400'}`}>
              {status?.email_enabled ? 'ACTIF' : 'DÉSACTIVÉ'}
            </span>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => toggle('email', !status?.email_enabled)}
              disabled={submitting}
              className={status?.email_enabled
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-success text-black hover:bg-green-300'}
            >
              <Power className="w-4 h-4 mr-2" />
              {status?.email_enabled ? 'Désactiver' : 'Activer'}
            </Button>
            {status?.email_enabled && (
              <Button onClick={() => sendTest('email')} disabled={submitting} variant="outline" className="text-xs">
                <Send className="w-3 h-3 mr-1" /> Test envoi
              </Button>
            )}
          </div>
        </motion.div>

        {/* SMS */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className={`glass-card border p-6 ${status?.sms_enabled ? 'border-success/40' : 'border-gray-700'}`}>
          <div className="flex items-center gap-3 mb-4">
            <MessageSquare className={`w-8 h-8 ${status?.sms_enabled ? 'text-success' : 'text-gray-500'}`} />
            <div>
              <h3 className="text-white font-bold">SMS (Brevo)</h3>
              <p className="text-xs text-gray-500">~0,07€/SMS payé à l'usage · Pas d'abonnement</p>
            </div>
          </div>
          <div className="text-sm text-gray-400 mb-4">
            Statut : <span className={`font-bold ${status?.sms_enabled ? 'text-success' : 'text-red-400'}`}>
              {status?.sms_enabled ? 'ACTIF' : 'DÉSACTIVÉ'}
            </span>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => toggle('sms', !status?.sms_enabled)}
              disabled={submitting}
              className={status?.sms_enabled
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-success text-black hover:bg-green-300'}
            >
              <Power className="w-4 h-4 mr-2" />
              {status?.sms_enabled ? 'Désactiver' : 'Activer'}
            </Button>
            {status?.sms_enabled && (
              <Button onClick={() => sendTest('sms')} disabled={submitting} variant="outline" className="text-xs">
                <Send className="w-3 h-3 mr-1" /> Test envoi (0,07€)
              </Button>
            )}
          </div>
        </motion.div>
      </div>

      {testResult && (
        <div className={`glass-card border p-4 ${testResult.status === 'sent' ? 'border-success/40' : 'border-red-400/40'}`}>
          <div className="text-sm text-gray-300">
            Test {testResult.channel} : <span className={testResult.status === 'sent' ? 'text-success' : 'text-red-400'}>
              {testResult.status}
            </span> → {testResult.to}
          </div>
          {testResult.error && (
            <div className="text-xs text-red-400 font-mono mt-2 bg-gray-900 p-2 rounded">{testResult.error}</div>
          )}
        </div>
      )}

      <div className="glass-card border border-gray-800 p-4 text-xs text-gray-500">
        <p className="mb-2 text-cyan-400 font-mono">// COMMENT ÇA MARCHE</p>
        <p className="mb-2">
          Le toggle DB <code className="text-cyan-400">system_flags.notifications</code> est lu à chaque envoi.
          Quand un canal est OFF, le code log l'envoi mais ne contacte pas Brevo — donc zéro coût même si ton service tente d'envoyer.
        </p>
        <p>
          Le bouton "Test envoi" déclenche un vrai envoi à toi-même (admin) pour vérifier que Brevo répond bien depuis Render.
          Pour le SMS test, il faut avoir un numéro renseigné dans Mon Compte.
        </p>
      </div>
    </div>
  );
};

export default NotificationsTogglePanel;
